import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, Calendar, Lightbulb } from 'lucide-react';
import SEO from '@/components/SEO';
import { Pill } from '@/components/flat';
import { useKeywords } from '@/hooks/useKeywords';
import { useExpiringDomains } from '@/hooks/useExpiringDomains';
import { useStats } from '@/hooks/useStats';
import EnhancedSearch from '@/components/EnhancedSearch';
import ResultsGrid from '@/components/ResultsGrid';
import ScanProgressBar from '@/components/ScanProgressBar';
import EmailCaptureModal, { SESSION_KEY } from '@/components/EmailCaptureModal';
import { useScan } from '@/contexts/ScanContext';
import { scanApi } from '@/services/api';
import type { DomainScanResult } from '@/types';
import { ScanLimitBanner } from '@/components/PremiumGate';
import PricingModal from '@/components/PricingModal';
import { useAuth } from '@/contexts/AuthContext';

// ── TopPicks ──────────────────────────────────────────────────────────────────

const TopPicks: React.FC<{ results: DomainScanResult[]; keyword: string }> = ({ results, keyword }) => {
  const top = results
    .filter(r => r.status === 'available')
    .sort((a, b) => (b.valuation?.estimatedValue.max ?? 0) - (a.valuation?.estimatedValue.max ?? 0))
    .slice(0, 4);

  if (top.length === 0) return null;

  return (
    <div className="bg-white border-b border-gray-100 px-4 sm:px-6 lg:px-8 py-4">
      <div className="max-w-7xl mx-auto">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Top Picks for "{keyword}"</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {top.map(r => {
            const [base, ...rest] = r.domain.split('.');
            const tld = rest.length ? '.' + rest.join('.') : '';
            const price = r.pricing?.purchasePrice ?? r.pricingSources?.[0]?.purchasePrice;
            return (
              <Link
                key={r.domain}
                to={`/domain/${encodeURIComponent(r.domain)}`}
                className="group flex flex-col border border-gray-200 rounded-lg px-4 py-3 hover:border-gray-400 transition-colors bg-white"
              >
                <span className="font-mono text-sm font-medium">
                  <span className="text-gray-400">{base}</span>
                  <span className="text-gray-900 font-bold">{tld}</span>
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  {price ? `$${price.toFixed(2)}` : 'Available'}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Widget shell ──────────────────────────────────────────────────────────────

function Widget({
  icon: Icon,
  title,
  to,
  toLinkLabel = 'View all →',
  children,
}: {
  icon: React.ElementType;
  title: string;
  to: string;
  toLinkLabel?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-md flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-blue-500 flex-shrink-0" />
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        </div>
        <Link to={to} className="text-xs text-blue-500 hover:text-blue-600 font-medium transition-colors whitespace-nowrap">
          {toLinkLabel}
        </Link>
      </div>
      <div className="flex-1 p-4">
        {children}
      </div>
    </div>
  );
}

// ── Skeleton rows ─────────────────────────────────────────────────────────────

function SkeletonRows({ n = 5 }: { n?: number }) {
  return (
    <div className="space-y-2.5">
      {Array.from({ length: n }).map((_, i) => (
        <div key={i} className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: `${70 + (i % 3) * 10}%` }} />
      ))}
    </div>
  );
}

// ── Keywords widget ───────────────────────────────────────────────────────────

function KeywordsWidget({ onSearch }: { onSearch?: (keyword: string) => void }) {
  const { data: keywords = [], isLoading: loading } = useKeywords(5);

  return (
    <Widget icon={TrendingUp} title="Top Keywords" to="/keywords" toLinkLabel="All keywords →">
      {loading ? <SkeletonRows /> : keywords.length === 0 ? (
        <p className="text-xs text-gray-400">No keyword data yet.</p>
      ) : (
        <ol className="space-y-2">
          {keywords.map((k, i) => (
            <li key={k.keyword}>
              {onSearch ? (
                <button
                  onClick={() => onSearch(k.keyword)}
                  className="flex items-center justify-between group w-full text-left"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-gray-400 w-4 flex-shrink-0 tabular-nums">{i + 1}.</span>
                    <span className="text-sm text-gray-800 group-hover:text-blue-600 transition-colors font-medium truncate">{k.keyword}</span>
                  </span>
                  <Pill variant="muted">{k.searchCount} searches</Pill>
                </button>
              ) : (
                <Link
                  to={`/?q=${encodeURIComponent(k.keyword)}`}
                  className="flex items-center justify-between group"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-xs text-gray-400 w-4 flex-shrink-0 tabular-nums">{i + 1}.</span>
                    <span className="text-sm text-gray-800 group-hover:text-blue-600 transition-colors font-medium truncate">{k.keyword}</span>
                  </span>
                  <Pill variant="muted">{k.searchCount} searches</Pill>
                </Link>
              )}
            </li>
          ))}
        </ol>
      )}
    </Widget>
  );
}

// ── Expiring domains widget ───────────────────────────────────────────────────

function ExpiringWidget() {
  const { data, isLoading: loading } = useExpiringDomains();
  const domains = (data ?? []).slice(0, 5);

  const formatDate = (s: string) => {
    try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return s; }
  };

  return (
    <Widget icon={Calendar} title="Expiring Domains" to="/expiring" toLinkLabel="Browse all →">
      {loading ? <SkeletonRows /> : domains.length === 0 ? (
        <p className="text-xs text-gray-400">No expiring domains data.</p>
      ) : (
        <div className="space-y-2">
          {domains.map(d => (
            <Link key={d.domain_name} to={`/domain/${encodeURIComponent(d.domain_name)}`}
              className="flex items-center justify-between group py-0.5">
              <span className="text-sm font-medium text-gray-800 group-hover:text-blue-600 transition-colors truncate mr-2">{d.domain_name}</span>
              <div className="flex items-center gap-2 flex-shrink-0">
                {d.price > 0 && <span className="text-xs text-gray-500">${d.price}</span>}
                <span className="text-xs text-gray-400">{formatDate(d.expiring_date)}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Widget>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { data: stats } = useStats();
  const totalDomains = stats?.total ?? null;
  const { dispatch, state } = useScan();
  const { plan } = useAuth();
  const [activeKeyword, setActiveKeyword] = useState('');
  const [scanStats, setScanStats] = useState({ processed: 0, total: 0, available: 0, isDone: false });
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [scanRemaining, setScanRemaining] = useState<number | null>(null);
  const [showPricing, setShowPricing] = useState(false);
  const startedKeyRef = useRef<string | null>(null);
  const didAutoStartRef = useRef(false);
  const cancelRef = useRef<(() => void) | null>(null);

  const isActive = !!activeKeyword;

  const startScan = useCallback(async (keyword: string, tlds?: string[]) => {
    if (!keyword.trim()) return;
    const key = [keyword.trim(), (tlds || []).join(',')].join('|');
    if (startedKeyRef.current === key) return;
    startedKeyRef.current = key;

    // Cancel any in-flight scan
    cancelRef.current?.();

    const kw = keyword.trim();
    let cancelled = false;
    cancelRef.current = () => { cancelled = true; };

    setActiveKeyword(kw);
    setScanStats({ processed: 0, total: 0, available: 0, isDone: false });
    dispatch({ type: 'CLEAR_RESULTS' });
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_SEARCHED_DOMAIN', payload: kw });
    // Update URL for shareability without adding a history entry
    window.history.replaceState(null, '', `/?q=${encodeURIComponent(kw)}`);

    await scanApi.scanVariantsStream(
      kw,
      { tlds: tlds && tlds.length > 0 ? tlds : undefined },
      (progress) => {
        if (cancelled) return;
        const { processed, total } = progress;
        const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
        setScanStats(prev => ({ ...prev, processed, total }));
        dispatch({ type: 'SET_PROGRESS', payload: { current: percent, total: 100, isScanning: percent > 0 && percent < 100 } });
        if (percent >= 1) dispatch({ type: 'SET_LOADING', payload: false });
        if (percent >= 100) dispatch({ type: 'SET_PROGRESS', payload: { current: 100, total: 100, isScanning: false } });
      },
      (results) => {
        if (cancelled) return;
        dispatch({ type: 'ADD_RESULTS', payload: results });
        dispatch({ type: 'SET_LOADING', payload: false });
        const newAvailable = results.filter(r => r.status === 'available').length;
        if (newAvailable > 0) {
          setScanStats(prev => ({ ...prev, available: prev.available + newAvailable }));
        }
      },
      () => {
        dispatch({ type: 'SET_PROGRESS', payload: { current: 100, total: 100, isScanning: false } });
        dispatch({ type: 'SET_LOADING', payload: false });
        setScanStats(prev => {
          if (!sessionStorage.getItem(SESSION_KEY)) {
            setTimeout(() => setShowEmailCapture(true), 1500);
          }
          return { ...prev, isDone: true };
        });
      },
      (error) => {
        if (cancelled) return;
        if ((error as any)?.code === 'SCAN_LIMIT' || error === 'Daily scan limit reached') {
          setScanRemaining(0);
          dispatch({ type: 'SET_LOADING', payload: false });
          setScanStats(prev => ({ ...prev, isDone: true }));
          return;
        }
        dispatch({ type: 'SET_ERROR', payload: error });
        dispatch({ type: 'SET_LOADING', payload: false });
        setScanStats(prev => ({ ...prev, isDone: true }));
      }
    );
  }, [dispatch]);

  // Auto-start scan from URL param — one-time on mount only
  useEffect(() => {
    if (didAutoStartRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const q = params.get('q');
    if (q && !activeKeyword) {
      didAutoStartRef.current = true;
      startScan(q);
    }
  }, []); // intentionally empty — reads URL once on mount

  const handleSearch = (input: string, searchType: 'keyword' | 'domain', tlds?: string[]) => {
    if (searchType === 'domain') {
      navigate(`/domain/${encodeURIComponent(input)}`);
    } else {
      startedKeyRef.current = null; // allow re-scanning even if same keyword
      startScan(input, tlds);
    }
  };

  return (
    <div className="flex flex-col flex-1">
      <SEO
        title="DomBase — Find Your Perfect Domain"
        description="Scan 40+ TLDs in seconds. No signup required. Domain availability, pricing, WHOIS and brand risk — all in one place."
        canonical="/"
        ogType="website"
        breadcrumbs={[{ name: 'Home', url: '/' }]}
      />

      {showPricing && (
        <PricingModal onClose={() => setShowPricing(false)} onSignIn={() => setShowPricing(false)} />
      )}

      {isActive ? (
        // ── Active state: scanning / has results ───────────────────────────────
        <>
          {/* Scan limit banner — shown for free users near/at their daily limit */}
          {plan !== 'premium' && scanRemaining !== null && (
            <ScanLimitBanner remaining={scanRemaining} onUpgrade={() => setShowPricing(true)} />
          )}

          {/* Sticky slim search bar */}
          <div className="sticky top-0 z-20 bg-white border-b border-gray-100 flex items-center gap-3 px-4 sm:px-6 h-14 flex-shrink-0">
            <input
              defaultValue={activeKeyword}
              key={activeKeyword}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const val = (e.target as HTMLInputElement).value.trim();
                  if (val) {
                    startedKeyRef.current = null;
                    startScan(val);
                  }
                }
              }}
              placeholder="Search domains…"
              className="flex-1 min-w-0 text-sm text-gray-900 placeholder-gray-400 bg-transparent outline-none"
            />
            {scanStats.isDone && (
              <span className="ml-auto text-sm text-gray-400 whitespace-nowrap flex-shrink-0">
                {scanStats.available > 0
                  ? `${scanStats.available} available`
                  : 'None available'}
              </span>
            )}
          </div>

          {/* Scan progress bar */}
          {(scanStats.total > 0 || scanStats.isDone) && (
            <ScanProgressBar
              processed={scanStats.processed}
              total={scanStats.total}
              available={scanStats.available}
              isDone={scanStats.isDone}
            />
          )}

          {/* Top Picks */}
          {scanStats.isDone && scanStats.available >= 1 && (
            <TopPicks results={state.results} keyword={activeKeyword} />
          )}

          {/* All-unavailable CTA */}
          {scanStats.isDone && scanStats.available === 0 && scanStats.processed > 0 && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 lg:px-8 py-3">
              <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    <span className="font-semibold">All {scanStats.processed} variants of "{activeKeyword}" are taken.</span>
                    {' '}Try a shorter keyword, add a prefix like <span className="font-mono">get</span>, <span className="font-mono">use</span>, or <span className="font-mono">try</span>, or pick a less common TLD.
                  </p>
                </div>
                <button
                  onClick={() => {
                    const suggestions = [`get${activeKeyword}`, `use${activeKeyword}`, `${activeKeyword}hq`, `${activeKeyword}app`];
                    const pick = suggestions[Math.floor(Math.random() * suggestions.length)];
                    startedKeyRef.current = null;
                    startScan(pick);
                  }}
                  className="sm:ml-auto flex-shrink-0 text-xs font-medium text-amber-700 underline underline-offset-2 hover:text-amber-900 whitespace-nowrap"
                >
                  Try a variation →
                </button>
              </div>
            </div>
          )}

          {/* Results */}
          <div className="flex-1 flex flex-col min-h-0">
            <ResultsGrid />
          </div>
        </>
      ) : (
        // ── Idle state: hero + widgets ─────────────────────────────────────────
        <>
          {/* Hero */}
          <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4">
            <div className="w-full max-w-2xl text-center">
              <h1 className="text-5xl font-bold tracking-tight">
                <span className="text-gray-900">Find your perfect</span>
                <br />
                <span className="text-gray-400">domain.</span>
              </h1>
              <p className="text-base text-gray-400 mt-3">Scan 40+ TLDs in seconds. No signup.</p>

              <div className="mt-8">
                <EnhancedSearch initialSearchType="keyword" onSubmit={handleSearch} />
              </div>

              {/* Trust stats */}
              <p className="text-sm text-gray-400 mt-6">
                {totalDomains !== null ? totalDomains.toLocaleString() : '—'} domains scanned
                {' · '}40+ TLDs
                {' · '}Free, no signup
              </p>
            </div>
          </div>

          {/* Secondary section */}
          <div className="pt-16 pb-24 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <KeywordsWidget onSearch={startScan} />
                <ExpiringWidget />
              </div>
            </div>
          </div>
        </>
      )}

      {showEmailCapture && (
        <EmailCaptureModal
          keyword={activeKeyword}
          availableCount={scanStats.available}
          onDismiss={() => setShowEmailCapture(false)}
        />
      )}
    </div>
  );
};

export default Home;
